import { useNotification } from "@expert/common-ui";
import { Box, Button, Flex, Text, TextInput } from "@mantine/core";
import { isEmail, useForm } from "@mantine/form";
import { type JSX, useState } from "react";

import type { CommunicationType, EligibleProduct } from "../../../../types";

import { isMaxNotificationAttemptsError } from "../../../../api/sendNotification.errors";
import { useSalesContext } from "../../../../sales-context/SalesContext";
import { formatPhoneNumber, sendSalesErrorMetric } from "../../../../shared-utils";
import { useIntegratedFlowStore, useProductOfferStore, useVerifiedCustomer } from "../../../../stores";
import { GreenQuote } from "../Icons";
import { SendDisclosuresIcon } from "../Icons/SendDisclosureIcon";
import { hasVerbatimToken, RichPhrase } from "../RichPhrase";
import { CommunicationTypeSelector } from "./CommunicationTypeSelector";
import { useHandleSendLink } from "./HandleSendLink";
import classes from "./SendLink.module.css";
import { SendLinkFooter } from "./SendLinkFooter";
import { getSendLinkCopy } from "./utils/getSendLinkCopy";

interface SendLinkProps {
    product: EligibleProduct;
    mdn: string;
    email: string;
    updateMDN: React.Dispatch<React.SetStateAction<string>>;
    updateEmail: React.Dispatch<React.SetStateAction<string>>;
    sendLinkCommTypeOverride?: CommunicationType;
    helpButtonEnabled?: boolean;
    isGuidedBrowsing?: boolean;
}

export function SendLink({
    product,
    mdn,
    email,
    updateMDN,
    updateEmail,
    sendLinkCommTypeOverride,
    helpButtonEnabled = false,
    isGuidedBrowsing,
}: SendLinkProps): JSX.Element | null {
    const { sessionId, logger, analytics, taskType, partner, features } = useSalesContext();
    const notification = useNotification();
    const integratedStep = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const updateIntegratedStep = useIntegratedFlowStore((state) => state.updateIntegratedStep);
    const customer = useVerifiedCustomer(sessionId);
    const [commType, setCommType] = useState<CommunicationType>(sendLinkCommTypeOverride ?? "SMS");
    const [loading, setLoading] = useState(false);
    const isEmailType = () => commType === "EMAIL";
    const isChat = taskType.isChat;
    const primaryProducts = useProductOfferStore((state) => state.getPrimaryProducts(sessionId));
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    const sendLink = useHandleSendLink({ isGuidedBrowsing });
    // Pilot copy carries Markdown and the ==verbatim== token; the hardcoded defaults never do, so they
    // keep rendering as the plain string they always have.
    const sendLinkCopy = getSendLinkCopy(
        sendLinkCommTypeOverride,
        product.productSku,
        features.isUsingMovedDiscoveryAndEngagementQuestions,
    );

    const sanitizePhoneNumber = (phone: string | undefined): string => {
        if (!phone) return "";
        return phone.replace(/\D/g, "");
    };

    const formatInputMDN = (inputMdn: string | undefined): string => {
        if (!inputMdn) return "";
        const sanitized = sanitizePhoneNumber(inputMdn);
        return `+1${sanitized}`;
    };

    const formatInputMDNDisplay = (inputMdn: string): string => {
        const sanitized = sanitizePhoneNumber(inputMdn);
        if (sanitized.length <= 3) return `(${sanitized}`;
        if (sanitized.length <= 6) return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3)}`;
        return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3, 6)}-${sanitized.slice(6, 10)}`;
    };

    const form = useForm({
        initialValues: {
            contact: isEmailType() ? email : formatPhoneNumber(mdn, undefined, true).number || mdn,
        },

        validate: {
            contact: (value) => {
                if (!value) {
                    return "This field is required";
                }
                if (isEmailType()) {
                    const validateEmail = isEmail("Invalid email address");
                    return validateEmail(value);
                } else {
                    try {
                        formatPhoneNumber(formatInputMDN(value));
                    } catch {
                        return "Invalid phone number";
                    }
                    return /^\d{10}$/.test(sanitizePhoneNumber(formatInputMDNDisplay(value)))
                        ? null
                        : "Invalid phone number";
                }
            },
        },
    });
    const handleContactChange = (inputValue: string) => {
        if (isEmailType()) {
            form.setFieldValue("contact", inputValue);
        } else {
            const formattedPhone = formatInputMDNDisplay(inputValue);
            form.setFieldValue("contact", formattedPhone);
        }
    };

    const handleSendLinkClick = async () => {
        if (form.validate().hasErrors) {
            logger.warn("Send link validation failed");
            return;
        }

        try {
            setLoading(true);
            if (isEmailType()) {
                updateEmail(form.values.contact);
            } else {
                updateMDN(form.values.contact);
            }

            const contactToSend = isEmailType() ? form.values.contact : formatInputMDN(form.values.contact);
            const isMDNUpdated = !isEmailType() && customer?.mdn !== contactToSend;
            if (isMDNUpdated) {
                void analytics.dispatcher.withExtra({ step: integratedStep }).dispatchBusinessEvent("MDN_Updated");
            }
            const response = await sendLink(commType, contactToSend);

            useProductOfferStore
                .getState()
                .updateSmartLinkMetaBySessionId(sessionId, selectedProduct?.id ?? primaryProducts[0].id, {
                    maximumSmartLinksGenerated: response.maxAttemptsReached,
                });

            void analytics.dispatcher
                .withExtra({ nextStep: integratedStep })
                .dispatchBusinessEvent("ButtonClicked_SendLink");
            const nextStep = product.confirmationCodePattern ? "InlineSaleTrackerStep" : "WaitingForEnrollmentStep";
            updateIntegratedStep(sessionId, nextStep);
        } catch (error) {
            if (isMaxNotificationAttemptsError(error)) {
                notification.error("You have reached the maximum number of attempts to send a link.");
                logger.warn(
                    { error },
                    "Failed to send link. You have reached the maximum number of attempts to send a link.",
                );
                // No sendSalesErrorMetric on a cap refusal — see isMaxNotificationAttemptsError
                // in api/sendNotification.errors.ts for why, and ew#7426 for the measured volume.
            } else {
                notification.error("Failed to send link. Please try again.");
                logger.warn({ error }, "Failed to send link. Please try again.");
                void sendSalesErrorMetric("SendLinkError", { partner });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Flex direction={"column"} justify={"space-between"} w="100%">
            {isChat ? (
                <Flex direction="column" px="sm" gap={12} justify={"center"} align="center">
                    <Box py="md">
                        <SendDisclosuresIcon />
                    </Box>
                    <Flex direction="column" gap={12} align="center">
                        <Text fz="h5" fw="700" c="dark.1">
                            Send the disclosures
                        </Text>
                        <Text size="xs" c="dark.2" ta="center">
                            Once you send the link, the customer will need to review and accept the disclosures.
                        </Text>
                    </Flex>
                </Flex>
            ) : (
                <Flex mt={24} mb={32} px={24} gap={24} direction="column" w="100%">
                    <Text fz="h5" fw="700">
                        Send a link for enrollment
                    </Text>
                    <Flex direction="column" gap="xs">
                        <Text size="xs">Please ask:</Text>
                        <Flex gap="xs">
                            <Flex>
                                <GreenQuote />
                            </Flex>
                            {hasVerbatimToken(sendLinkCopy) ? (
                                <Text component="div" size="xs">
                                    <RichPhrase markdown={sendLinkCopy} />
                                </Text>
                            ) : (
                                <Text size="xs">{sendLinkCopy}</Text>
                            )}
                        </Flex>
                    </Flex>
                    {!sendLinkCommTypeOverride && (
                        <CommunicationTypeSelector
                            commType={commType}
                            setCommType={setCommType}
                            form={form}
                            email={email ?? ""}
                            mdn={mdn}
                        />
                    )}
                    <form
                        onSubmit={(event) => {
                            event.preventDefault();
                        }}
                    >
                        <TextInput
                            className="fs-mask"
                            classNames={{ input: classes.textAreaStyle }}
                            key={commType}
                            label={commType === "EMAIL" ? "Email address" : "Phone number"}
                            {...form.getInputProps("contact")}
                            size="xs"
                            onChange={(event) => handleContactChange(event.currentTarget.value)}
                        />
                        {helpButtonEnabled && (
                            <Button
                                onClick={() => updateIntegratedStep(sessionId, "HelpCustomerFindSubscribeButtonStep")}
                                classNames={{ root: classes.linkHelpButton }}
                                size="xxs"
                                p={0}
                                variant="transparent"
                            >
                                <Text size="xs" td="underline">
                                    Continue without sending link
                                </Text>
                            </Button>
                        )}
                    </form>
                </Flex>
            )}
            <SendLinkFooter
                handleSendLinkClick={() => {
                    void handleSendLinkClick();
                }}
                isCommunicationInfoCollected={form.isValid()}
                isLoading={loading}
                integratedStep={integratedStep}
                isChat={isChat}
            />
        </Flex>
    );
}
